import type { ChangeEvent } from 'react'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import TextLink from 'LuxKit/TextLink'
import LineBugIcon from 'LuxKit/Icons/line/LineBugIcon'
import LineSearchIcon from 'LuxKit/Icons/line/LineSearchIcon'
import IconButton from 'LuxKit/Button/IconButton'
import Group from 'components/utils/Group'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import ModalContext from 'contexts/ModalContext'
import LogoIcon from 'components/App/Header/Logo/LogoIcon'
import Clickable from 'components/Common/Clickable'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import noop from 'lib/function/noop'
import type { TripPlannerChat } from 'tripPlanner/api/types'
import type { ClassicTripHistoryItem } from 'tripPlanner/legacyArchive/useClassicTripHistory'
import Switch from 'LuxKit/Switch/Switch'
import Sheet from 'components/utils/Sheet'
import { buildRecentChatItems, getRecentChatItemUrl, type RecentChatItem } from 'tripPlanner/pages/chat/recentChatItems'
import DrawerRecentChatListItem from './DrawerRecentChatListItem'
import TripPlannerMobileHeaderTitle from './TripPlannerMobileHeaderTitle'
import TripPlannerDrawerFooter from './TripPlannerDrawerFooter'
import TripPlannerChatSearchResults from './TripPlannerChatSearchResults'
import TextInput from 'components/Common/Form/Input/TextInput'
import TripPlannerChatSearchModal from './TripPlannerChatSearchModal'

const MOBILE_VARIANT_CLASS_NAME = 'mobile'

const HeaderLogoWrapper = styled.div`
  display: flex;
  align-items: center;
`

const DrawerModalBody = styled(ModalBody)`
  overflow: hidden;
  min-height: 0;
`

const DrawerModalContent = styled(ModalContent)`
  display: flex;
  flex-direction: column;
  min-height: 0;

  &.${MOBILE_VARIANT_CLASS_NAME} {
    padding: 0;
  }
`

const DrawerLayout = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: ${rem(12)};
  min-height: 0;

  &.${MOBILE_VARIANT_CLASS_NAME} {
    gap: 0;
  }
`

const ChatsSection = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: ${rem(16)};
  min-height: 0;

  &.${MOBILE_VARIANT_CLASS_NAME} {
    padding: ${rem(16)} ${rem(20)} 0;
  }
`

const ChatHistoryList = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;

  &.${MOBILE_VARIANT_CLASS_NAME} {
    margin: 0 -${rem(20)};
  }
`

const DrawerBottomControls = styled.div`
  display: flex;
  flex-shrink: 0;
  flex-direction: column;
  gap: ${rem(28)};

  &.${MOBILE_VARIANT_CLASS_NAME} {
    gap: ${rem(8)};
    padding: ${rem(12)} ${rem(16)} calc(${rem(12)} + env(safe-area-inset-bottom));
  }
`

interface Props {
  variant?: 'default' | 'mobile'
  onStartNewChat: () => void
  isLoggedIn?: boolean
  recentChats?: Array<TripPlannerChat>
  recentTrips?: Array<ClassicTripHistoryItem>
  isRecentItemsLoading?: boolean
  onChatSelect?: (chatId: number) => void
  onChatRename?: (chatId: number, name: string) => Promise<void>
  onChatDelete?: (chatId: number) => Promise<void> | void
  onSearchChats?: () => void
  activeChatId?: number
  onActiveChatDownloadPdf?: () => void
  onActiveChatDownloadSpreadsheet?: () => void
  // Staff tools
  isStaff?: boolean
  isDevMode?: boolean
  onDevModeToggle?: () => void
  isRewindMode?: boolean
  onRewindModeToggle?: () => void
  onCloseReady?: (closeDrawer: () => void) => void
}

function TripPlannerDrawer({
  variant = 'default',
  onStartNewChat,
  isLoggedIn = false,
  recentChats,
  recentTrips,
  isRecentItemsLoading = false,
  onChatSelect,
  onChatRename,
  onChatDelete,
  onSearchChats,
  activeChatId,
  onActiveChatDownloadPdf,
  onActiveChatDownloadSpreadsheet,
  isStaff = false,
  isDevMode = false,
  onDevModeToggle,
  isRewindMode = false,
  onRewindModeToggle,
  onCloseReady,
}: Props) {
  const intl = useIntl()
  const { resolve } = useModalElementContext()
  const showModal = useContext(ModalContext)
  const [searchQuery, setSearchQuery] = useState('')
  const isSearching = searchQuery.trim().length >= 2
  const handleQueryChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.currentTarget.value)
  }, [])
  const isMobileVariant = variant === 'mobile'
  const mobileVariantClassName = isMobileVariant ? MOBILE_VARIANT_CLASS_NAME : undefined

  const closeDrawer = useCallback(() => {
    resolve()
  }, [resolve])

  const handleSearchResultSelect = useCallback((chatId: number) => {
    closeDrawer()
    onChatSelect?.(chatId)
  }, [closeDrawer, onChatSelect])

  useEffect(() => {
    onCloseReady?.(closeDrawer)
    return () => onCloseReady?.(noop)
  }, [closeDrawer, onCloseReady])

  const handleNewChat = useCallback(() => {
    closeDrawer()
    onStartNewChat()
  }, [closeDrawer, onStartNewChat])

  const handleSearchChats = useCallback(() => {
    if (onSearchChats) {
      onSearchChats()
      closeDrawer()
      return
    }

    showModal(
      <TripPlannerChatSearchModal
        recentChats={recentChats}
        onChatSelect={onChatSelect ?? noop}
      />,
    )
    closeDrawer()
  }, [closeDrawer, onChatSelect, onSearchChats, recentChats, showModal])

  const handleRecentItemClick = useCallback((item: RecentChatItem) => (event: React.MouseEvent) => {
    const isModifiedClick = event.metaKey || event.ctrlKey || event.shiftKey || event.altKey
    if (isModifiedClick) {
      return
    }

    closeDrawer()
    if (item.type === 'aiChat') {
      event.preventDefault()
      onChatSelect?.(item.id)
    }
  }, [closeDrawer, onChatSelect])

  const handleDeleteClick = useCallback(
    (item: RecentChatItem, closeMenu: () => void) => (event: React.MouseEvent) => {
      event.preventDefault()
      event.stopPropagation()
      closeMenu()
      closeDrawer()
      if (item.type === 'aiChat') {
        onChatDelete?.(item.id)
        return
      }
    },
    [closeDrawer, onChatDelete],
  )

  const handleRename = useCallback(
    async(item: RecentChatItem, name: string) => {
      if (item.type === 'aiChat') {
        await onChatRename?.(item.id, name)
        return
      }
    },
    [onChatRename],
  )

  const handleDevModeToggle = useCallback((_event: ChangeEvent<HTMLInputElement>) => {
    closeDrawer()
    onDevModeToggle?.()
  }, [closeDrawer, onDevModeToggle])

  const handleRewindModeToggle = useCallback((_event: ChangeEvent<HTMLInputElement>) => {
    closeDrawer()
    onRewindModeToggle?.()
  }, [closeDrawer, onRewindModeToggle])

  const allRecentItems = useMemo(() => {
    return buildRecentChatItems(recentChats ?? [], recentTrips ?? [])
  }, [recentChats, recentTrips])
  const hasRecentItems = allRecentItems.length > 0
  const showRecentItemsLoading = isRecentItemsLoading && !hasRecentItems
  const chatsLabel = intl.formatMessage({
    id: 'x3JBSvTY-jXIoAlF0Kiw',
    defaultMessage: 'Chats',
    description: 'Section heading for the chats list in the trip planner drawer',
  })
  const createNewChatLabel = intl.formatMessage({
    id: 'hMLFrteXDG4txmeo29R7',
    defaultMessage: 'Create new chat',
    description: 'Button label to start a new chat in the trip planner drawer',
  })
  const searchChatsLabel = intl.formatMessage({
    id: 'pOSBCAxfDZJRnHBv_PCF',
    defaultMessage: 'Search chats',
    description: 'Accessible label for the TripPlanner chat search button',
  })
  const planWithAiChatHeading = intl.formatMessage({
    id: 'LBjr7kIwyT6PI1I_-koY',
    defaultMessage: 'Plan with AI chat',
    description: 'Heading for the empty state when no chats exist in the drawer',
  })
  const planWithAiChatBody = intl.formatMessage({
    id: 'w3ssyhVL6Z7hEsPnhNhc',
    defaultMessage: 'Explore ideas, shape your trip, and refine plans as you go.',
    description: 'Description for the empty state when no chats exist in the drawer',
  })
  const staffToolsText = intl.formatMessage({
    id: 'MVS8v_NHiDbWbR0NuItv',
    defaultMessage: 'Staff tools',
    description: 'Label for the staff-only tools section in the TripPlanner navigation drawer',
  })

  const devModeText = intl.formatMessage({
    id: 'dXMxtVabgKHFm0Fe0oMH',
    defaultMessage: 'Dev mode',
    description: 'Toggle label for enabling developer mode in the TripPlanner navigation drawer',
  })

  const rewindModeText = intl.formatMessage({
    id: 'XMDtjOTHOn3v40fnGpnv',
    defaultMessage: 'Rewind mode',
    description: 'Toggle label for enabling staff rewind features in the TripPlanner navigation drawer',
  })

  const homepageLabel = intl.formatMessage({
    id: 'kEk902QolS5xO3-CySer',
    defaultMessage: 'Back to homepage',
    description: 'Accessibility label for the TripPlanner drawer header logo linking to the Luxury Escapes homepage',
  })

  const headerTitle = useMemo(() => (
    <HeaderLogoWrapper>
      <Clickable to="/" onClick={closeDrawer} aria-label={homepageLabel}>
        {isMobileVariant ? <TripPlannerMobileHeaderTitle /> : <LogoIcon height={24} desktopVariant="icon+text" />}
      </Clickable>
    </HeaderLogoWrapper>
  ), [closeDrawer, homepageLabel, isMobileVariant])

  const staffTools = isStaff && (
    <Group direction="vertical" gap={12}>
      <BodyText variant="medium" weight="bold">{staffToolsText}</BodyText>

      <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
        <Group direction="horizontal" gap={8} verticalAlign="center">
          <LineBugIcon />
          <BodyText variant="medium">{devModeText}</BodyText>
        </Group>
        <Switch
          checked={isDevMode}
          onChange={handleDevModeToggle}
        />
      </Group>
      <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
        <Group direction="horizontal" gap={8} verticalAlign="center">
          <LineBugIcon />
          <BodyText variant="medium">{rewindModeText}</BodyText>
        </Group>
        <Switch
          checked={isRewindMode}
          onChange={handleRewindModeToggle}
        />
      </Group>
    </Group>
  )

  return (
    <ModalBase mode="menu">
      <ModalHeader title={headerTitle} onCloseButtonClick={closeDrawer} />
      <DrawerModalBody>
        <DrawerModalContent className={mobileVariantClassName} flushListItems>
          <DrawerLayout className={mobileVariantClassName}>
            <ChatsSection className={mobileVariantClassName}>
              {isLoggedIn && !isMobileVariant && (
                <TextInput
                  type="search"
                  value={searchQuery}
                  onChange={handleQueryChange}
                  placeholder={searchChatsLabel}
                  aria-label={searchChatsLabel}
                  endIcon={<LineSearchIcon />}
                  noValidationSpacing
                />
              )}
              <Group direction="horizontal" gap={12} horizontalAlign="space-between" verticalAlign="center">
                {isMobileVariant && (
                  <BodyText variant="medium" weight="bold" colour="light-foreground-alt">
                    {chatsLabel}
                  </BodyText>
                )}
                {!isMobileVariant && (
                  <BodyText variant="small" colour="light-foreground-alt">
                    {chatsLabel}
                  </BodyText>
                )}
                <Group direction="horizontal" gap={8} verticalAlign="center">
                  {isLoggedIn && isMobileVariant && (
                    <IconButton
                      kind="tertiary"
                      size="small"
                      aria-label={searchChatsLabel}
                      onClick={handleSearchChats}
                    >
                      <LineSearchIcon />
                    </IconButton>
                  )}
                  {isMobileVariant && (
                    <TextLink size="caption" colour="normal-contrast" onClick={handleNewChat}>
                      {createNewChatLabel}
                    </TextLink>
                  )}
                  {!isMobileVariant && (
                    <TextButton kind="tertiary" size="small" onClick={handleNewChat}>
                      {createNewChatLabel}
                    </TextButton>
                  )}
                </Group>
              </Group>

              {isSearching && !isMobileVariant && (
                <TripPlannerChatSearchResults
                  query={searchQuery}
                  inline
                  onChatSelect={handleSearchResultSelect}
                />
              )}
              {!isSearching && hasRecentItems && (
                <ChatHistoryList className={mobileVariantClassName}>
                  {allRecentItems.map(item => (
                    <DrawerRecentChatListItem
                      key={`${item.type}-${item.id}`}
                      item={item}
                      subtitle={item.subtitle}
                      description={item.type === 'aiChat' ? item.description : undefined}
                      to={getRecentChatItemUrl(item)}
                      onClick={handleRecentItemClick(item)}
                      onRename={handleRename}
                      hideActions={item.type === 'classicTrip'}
                      onDeleteClick={handleDeleteClick}
                      onDownloadPdf={item.type === 'aiChat' && item.id === activeChatId ? onActiveChatDownloadPdf : undefined}
                      onDownloadSpreadsheet={item.type === 'aiChat' && item.id === activeChatId ? onActiveChatDownloadSpreadsheet : undefined}
                    />
                  ))}
                </ChatHistoryList>
              )}
              {!isSearching && showRecentItemsLoading && (
                <LoadingIndicator
                  data-testid="trip-planner-drawer-recent-items-loading"
                  inline
                  backdrop="none"
                  height={96}
                />
              )}
              {!isSearching && !isRecentItemsLoading && !hasRecentItems && (
                <Sheet padding="12 20" background="light-background" borderRadius="M">
                  <Group direction="vertical" gap={2}>
                    <BodyText variant="medium" weight="bold">
                      {planWithAiChatHeading}
                    </BodyText>
                    <BodyText variant="small" colour="light-foreground-alt">
                      {planWithAiChatBody}
                    </BodyText>
                  </Group>
                </Sheet>
              )}
            </ChatsSection>

            <DrawerBottomControls className={mobileVariantClassName}>
              <TripPlannerDrawerFooter
                closeDrawer={closeDrawer}
                staffTools={staffTools}
              />
            </DrawerBottomControls>
          </DrawerLayout>
        </DrawerModalContent>
      </DrawerModalBody>
    </ModalBase>
  )
}

export default TripPlannerDrawer
